import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Link, styled, Tooltip } from '@mui/material';
import {
    DataGridPremium,
    GridColDef,
    GridRowParams,
    useGridApiRef,
} from '@mui/x-data-grid-premium';

import { useAppSelector } from '../../state/hooks';
import { Shipper } from '../../interfaces/services/shippers';
import { ApiStatus } from '../../utils/enums';
import { formatNumber, formatCurrency } from '../../utils/formattingUtils';
import { buildShipperLink } from '../../utils/navigationHelpers';
import { useFilters } from '../Filters/useFilters';
import TableHeader from './TableHeader';

const classesPrefix = 'shipperTable';

const classes = {
    root: `${classesPrefix}-root`,
    tableContents: `${classesPrefix}-tableContents`,
    laneRow: `${classesPrefix}-laneRow`,
};

const StyledDiv = styled('div')(({ theme }) => {
    return {
        [`&.${classes.root}`]: {
            borderRadius: 0,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            height: '100%',
        },
        [`& .${classes.tableContents}`]: {
            height: '175px',
            flexGrow: 1,
            position: 'relative',
            '& .MuiDataGrid-root .MuiDataGrid-cell:focus': {
                outline: 0,
            },
            '& .MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
                outline: 0,
            },
            '& .MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within': {
                outline: 0,
            },
        },
        [`& .${classes.laneRow}`]: {
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: theme.palette.grey[0],
            },
        },
    };
});

const ShipperTable = (): React.JSX.Element => {
    const navigate = useNavigate();
    const filters = useFilters();
    const apiRef = useGridApiRef();

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);

    const shippersApiStatus = useAppSelector((state) => { return state.shippers.shippersApiStatus; });
    const shippers = useAppSelector((state) => { return state.shippers.shippers; });
    const shippersCount = useAppSelector((state) => { return state.shippers.shippersCount; });

    /** go to the shipper details page */
    const handleRowClick = (shipperId: string): void => {
        navigate(buildShipperLink(shipperId, filters));
    };

    const exportCsv = (allColumns: boolean): void => {
        apiRef.current.exportDataAsCsv({
            fileName: 'Network Insights - Shippers',
            allColumns,
        });
    };

    const columnSearchPredicate = (column: GridColDef, searchValue: string): boolean => {
        return (
            (column.headerName || column.field).toLowerCase().indexOf(searchValue) > -1 ||
            (column.description || '').toLowerCase().indexOf(searchValue) > -1
        );
    };

    const columns = useMemo((): GridColDef<Shipper>[] => {
        return [
            {
                headerName: 'Shipper Name',
                valueGetter: (_value, row): string => {
                    return row.pickup_party_name;
                },
                renderCell: ({ row }): React.JSX.Element => {
                    return (
                        <Tooltip title={row.pickup_party_name}>
                            <Link
                                component={RouterLink}
                                to={buildShipperLink(row.pickup_party_id, filters)}
                                onClick={(event): void => {
                                    event.stopPropagation();
                                }}
                            >
                                {row.pickup_party_name}
                            </Link>
                        </Tooltip>
                    );
                },
                field: 'pickup_party_name',
                flex: 1,
                minWidth: 200,
            },
            {
                headerName: 'Outbound Orders',
                valueFormatter: (value): string => {
                    return formatNumber(value);
                },
                field: 'freight_order_count',
                flex: 1,
                type: 'number',
                minWidth: 200,
            },
            {
                headerName: 'Total Revenue',
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                field: 'total_revenue_usd_amount',
                flex: 1,
                type: 'number',
            },
            {
                headerName: 'Revenue/Order',
                description: 'Revenue Per Order',
                valueGetter: (_value, row): number | null => {
                    return row.total_revenue_usd_amount !== null && row.freight_order_count ?
                        row.total_revenue_usd_amount / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                field: 'avg_revenue',
                flex: 1,
                type: 'number',
            },
            {
                headerName: 'Total RPM',
                description: 'Total Rate Per Mile',
                valueGetter: (_value, row): number | null => {
                    return row.total_revenue_usd_amount !== null && row.loaded_distance_miles_amount ?
                        row.total_revenue_usd_amount / row.loaded_distance_miles_amount :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                field: 'rpm',
                type: 'number',
                width: 150,
            },
            {
                headerName: 'LOH',
                description: 'Length of Haul',
                valueGetter: (_value, row): number | null => {
                    return row.loaded_distance_miles_amount !== null && row.freight_order_count ?
                        row.loaded_distance_miles_amount / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 0, 0);
                },
                field: 'loaded_distance_miles_amount',
                type: 'number',
                width: 110,
            },
            {
                headerName: 'Yield',
                description: 'Network based margin per load per day',
                valueFormatter: (value): string => {
                    return formatCurrency(value, 0, 0);
                },
                field: 'average_pickup_party_order_yield_usd',
                type: 'number',
                width: 110,
            },
            {
                headerName: 'RPH',
                description: 'Revenue Per Hour (in USD)',
                valueGetter: (_value, row): number | null => {
                    return row.total_revenue_usd_amount !== null && row.first_stop_departure_to_final_stop_arrival_hours_duration ?
                        row.total_revenue_usd_amount / row.first_stop_departure_to_final_stop_arrival_hours_duration :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                field: 'rph',
                flex: 1,
                type: 'number',
                minWidth: 170,
            },
            {
                headerName: 'MPH',
                description: 'Miles Per Hour',
                valueGetter: (_value, row): number | null => {
                    return row.loaded_distance_miles_amount !== null && row.first_stop_departure_to_final_stop_arrival_hours_duration ?
                        row.loaded_distance_miles_amount / row.first_stop_departure_to_final_stop_arrival_hours_duration :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                field: 'mph',
                flex: 1,
                type: 'number',
                minWidth: 170,
            },
            {
                headerName: 'Empty Miles',
                valueGetter: (_value, row): number | null => {
                    return row.empty_distance_miles_amount !== null && row.freight_order_count ?
                        row.empty_distance_miles_amount / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 1, 1);
                },
                field: 'empty_distance_miles_amount',
                flex: 1,
                type: 'number',
                minWidth: 185,
            },
            {
                headerName: 'In Transit Delay',
                description: 'In Transit Delay (in hours)',
                valueGetter: (_value, row): number | null => {
                    return row.netwise_in_transit_delay_hours_duration !== null && row.freight_order_count ?
                        row.netwise_in_transit_delay_hours_duration / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                field: 'netwise_in_transit_delay_hours_duration',
                flex: 1,
                type: 'number',
                minWidth: 210,
            },
        ];
    }, [filters]);

    return (
        <StyledDiv className={classes.root}>
            <TableHeader
                title='Shippers'
                itemCount={shippersCount}
                loading={shippersApiStatus === ApiStatus.Idle || shippersApiStatus === ApiStatus.Loading}
                handleDownload={exportCsv}
            />
            <div className={classes.tableContents}>
                <DataGridPremium
                    classes={{
                        row: classes.laneRow,
                    }}
                    apiRef={apiRef}
                    rows={shippers}
                    // rowCount={shippersCount}
                    columns={columns}
                    density='compact'
                    disableRowSelectionOnClick
                    disableRowGrouping
                    disableAggregation
                    loading={shippersApiStatus === ApiStatus.Idle || shippersApiStatus === ApiStatus.Loading}
                    paginationModel={{
                        page,
                        pageSize,
                    }}
                    onPaginationModelChange={(model): void => {
                        setPage(model.page);
                        setPageSize(model.pageSize);
                    }}
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    pagination
                    initialState={{
                        density: 'compact',
                    }}
                    slotProps={{
                        columnsManagement: {
                            searchPredicate: columnSearchPredicate,
                        },
                    }}
                    getRowId={(row): string => {
                        return row.pickup_party_id;
                    }}
                    onRowClick={(params: GridRowParams<Shipper>): void => {
                        handleRowClick(params.row.pickup_party_id);
                    }}
                />
            </div>
        </StyledDiv>
    );
};

export default ShipperTable;
